import React, { FC, useEffect, useState } from 'react';
import { BankAccount, CardToCardSettings } from 'interfaces';
import { Spin } from 'antd';
import { BsChevronDown } from 'react-icons/bs';
import { CardNumberSelectionModal } from '../../cardNumberSelectionModal/cardNumberSelectionModal.index';
import { getCardNumbers, getCardToCardSettings } from '../../../../../services';
import { copyToClipboard, detectBank, detectBankByName, formatCardNumber } from 'scripts';
import { TbCopy } from 'react-icons/tb';
import { Button } from '30pay/components';
import { FaPlay } from 'react-icons/fa';
import { CardToCardVideoModal } from './modal/video/videoModal.index';
import { CardToCardSelectTargetCardModal } from './modal/selectTargetBankModal/selectTargetBankModal.index';

interface Props {}

export const BuyPaymentCardToCardMethod: FC<Props> = (props) => {
    const [cardNumbersLoading, setCardNumbersLoading] = useState(true);
    const [cardNumbers, setCardNumbers] = useState<Omit<BankAccount, 'iBan'>[]>();
    const [selectedCardNumber, setSelectedCardNumber] = useState<Omit<BankAccount, 'iBan'>>();
    const [cardToCardSettingsLoading, setCardToCardSettingsLoading] = useState<boolean>(false);
    const [cardToCardSettings, setCardToCardSettings] = useState<CardToCardSettings[]>();
    const [selectedCardToCardSetting, setSelectedCardToCardSetting] = useState<CardToCardSettings>();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [videoModalOpen, setVideoModalOpen] = useState<boolean>(false);
    const [selectTargetCardModalOpen, setSelectTargetCardModalOpen] = useState(false);

    const onCardNumberSelection = (cardNumber: Omit<BankAccount, 'iBan'>) => {
        setSelectedCardNumber(cardNumber);
        setModalOpen(false);
        setCardToCardSettings(undefined);
        setSelectedCardToCardSetting(undefined);
    };

    const onGetCardToCardSettings = () => {
        getCardToCardSettings(setCardToCardSettingsLoading, setCardToCardSettings);
    };

    useEffect(() => {
        if (cardToCardSettings && cardToCardSettings.length !== 0) setSelectedCardToCardSetting(cardToCardSettings[0]);
    }, [cardToCardSettings]);

    useEffect(() => {
        getCardNumbers(setCardNumbersLoading, setCardNumbers, setSelectedCardNumber);
    }, []);

    return (
        <div className={'flex flex-col'}>
            <CardToCardSelectTargetCardModal
                isOpen={selectTargetCardModalOpen}
                onClose={() => setSelectTargetCardModalOpen(false)}
                onSelect={(e) => setSelectedCardToCardSetting(e)}
                data={cardToCardSettings}
            />
            {cardNumbers && (
                <CardNumberSelectionModal
                    open={modalOpen}
                    setOpen={setModalOpen}
                    cardNumbers={cardNumbers}
                    onSelect={onCardNumberSelection}
                />
            )}
            <CardToCardVideoModal isOpen={videoModalOpen} onClose={() => setVideoModalOpen(false)} />
            <label className={'max-lg:text-sm'}>شماره کارت:</label>
            <Spin spinning={cardNumbersLoading}>
                <div
                    onClick={() => setModalOpen(true)}
                    className={
                        'flex justify-between items-center border border-primary-40 rounded-[28px] text-base px-6 h-14 max-lg:h-12 max-lg:text-sm mt-2 clickable'
                    }
                >
                    {selectedCardNumber && (
                        <>
                            <div className={'flex items-center gap-4'}>
                                <img
                                    src={detectBank(selectedCardNumber?.cardNumber)?.bank_logo}
                                    className={'w-6 h-6'}
                                />

                                <p className="text-base font-bold text-darkPurple-100 flex-1">
                                    {selectedCardNumber?.bank?.name}
                                </p>
                            </div>
                            <p className="font-medium text-base text-darkPurple-100">
                                {formatCardNumber(selectedCardNumber?.cardNumber)}
                            </p>

                            <BsChevronDown />
                        </>
                    )}
                </div>
            </Spin>

            {!cardToCardSettings && (
                <Button
                    className={'mt-6'}
                    loading={cardToCardSettingsLoading}
                    type={'primary'}
                    disabled={cardToCardSettingsLoading || !selectedCardNumber}
                    onClick={onGetCardToCardSettings}
                >
                    دریافت اطلاعات واریز
                </Button>
            )}
            {selectedCardToCardSetting && (
                <>
                    <label className={'mt-6 max-lg:text-sm'}>بانک مقصد:</label>
                    <Spin spinning={cardToCardSettingsLoading}>
                        <div
                            onClick={() => setSelectTargetCardModalOpen(true)}
                            className={
                                'flex gap-4 items-center border border-primary-40 rounded-[28px] text-base px-6 h-14 max-lg:h-12 max-lg:text-sm mt-2 clickable'
                            }
                        >
                            <div className={'flex items-center gap-4'}>
                                <img
                                    src={detectBankByName(selectedCardToCardSetting?.bankName)?.bank_logo}
                                    className={'w-6 h-6'}
                                />
                            </div>
                            <p className="text-base font-bold text-darkPurple-100 flex-1">
                                حساب بانکی سی پی - {selectedCardToCardSetting.bankName}
                            </p>
                            <BsChevronDown />
                        </div>
                    </Spin>
                    <div className="flex flex-col gap-4">
                        <div className={'flex flex-col p-4 rounded-lg bg-background mt-6'}>
                            <span className={'font-bold text-darkPurple-100 text-base'}>اطلاعات مقصد</span>
                            <ul className={'flex flex-col gap-2 mt-2'}>
                                {selectedCardToCardSetting?.cardNumber ? (
                                    <li className={'flex items-center justify-between'}>
                                        <span className={'font-bold text-sm'}>شماره کارت مقصد</span>
                                        <span className={'flex items-center gap-2 font-bold text-sm'}>
                                            {formatCardNumber(selectedCardToCardSetting?.cardNumber)}
                                            <TbCopy
                                                className={'clickable'}
                                                onClick={() =>
                                                    copyToClipboard(selectedCardToCardSetting?.cardNumber, 'شماره کارت')
                                                }
                                            />
                                        </span>
                                    </li>
                                ) : null}
                                {selectedCardToCardSetting?.iBan ? (
                                    <li className={'flex items-center justify-between'}>
                                        <span className={'font-bold text-sm'}>شماره شبا مقصد</span>
                                        <span className={'flex items-center gap-2 font-bold text-sm'}>
                                            {selectedCardToCardSetting?.iBan}
                                            <TbCopy
                                                className={'clickable'}
                                                onClick={() =>
                                                    copyToClipboard(selectedCardToCardSetting?.iBan, 'شماره شبا مقصد')
                                                }
                                            />
                                        </span>
                                    </li>
                                ) : null}
                                {selectedCardToCardSetting?.accountNumber ? (
                                    <li className={'flex items-center justify-between'}>
                                        <span className={'font-bold text-sm'}>شماره حساب مقصد</span>
                                        <span className={'flex items-center gap-2 font-bold text-sm'}>
                                            {selectedCardToCardSetting?.accountNumber}
                                            <TbCopy
                                                className={'clickable'}
                                                onClick={() =>
                                                    copyToClipboard(
                                                        selectedCardToCardSetting?.accountNumber,
                                                        'شماره حساب مقصد',
                                                    )
                                                }
                                            />
                                        </span>
                                    </li>
                                ) : null}
                                {selectedCardToCardSetting?.ownerName ? (
                                    <li className={'flex items-center justify-between'}>
                                        <span className={'font-bold text-sm'}>نام صاحب حساب مقصد</span>
                                        <span className={'flex items-center gap-2 font-bold text-sm'}>
                                            {selectedCardToCardSetting?.ownerName}
                                            <TbCopy
                                                className={'clickable'}
                                                onClick={() =>
                                                    copyToClipboard(
                                                        selectedCardToCardSetting?.ownerName,
                                                        'نام صاحب حساب مقصد',
                                                    )
                                                }
                                            />
                                        </span>
                                    </li>
                                ) : null}
                                {selectedCardToCardSetting?.bankName ? (
                                    <li className={'flex items-center justify-between'}>
                                        <span className={'font-bold text-sm'}>نام بانک حساب مقصد</span>
                                        <span className={'flex items-center gap-2 font-bold text-sm'}>
                                            {selectedCardToCardSetting?.bankName}
                                        </span>
                                    </li>
                                ) : null}
                            </ul>
                        </div>
                        <Button
                            className="flex items-center gap-2"
                            type="default"
                            onClick={() => setVideoModalOpen(true)}
                        >
                            <p className="!-mb-1">ویدیو راهنما </p>
                            <FaPlay />
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};
